'use client';

import { FC, memo } from 'react';
import {
  ButtonsBox,
  CardHead,
  ContentWrapper,
  ImageWrapper,
  InfoWrapper,
  LocationWrapper,
  ProjectBottomBar,
  ProjectGalleryGrid,
  ProjectGalleryMain,
  ProjectGalleryThumb,
  ProjectGalleryMore,
  ProjectObjectsList,
  ProjectObjectsRow,
  ProjectObjectThumb,
  ProjectObjectsTitle,
  ProjectQuickRequestButton,
  ProjectViewButton,
  StyledButton,
  Wrapper,
  ProjectOfficeName,
  ProjectCardTitle,
  ProjectCardDescription,
  BrokerInfoContainer,
  BrokerImage,
  BrokerInfoColumn,
  ProjectButtonsContainer,
} from './styles';
import { ProjectCardStats } from '@/components/shared/HousingCardStats/ProjectCardStats';
import { Typography } from '@mui/material';
import { IHousingObjectResponse, OfferType } from '@/types';
import { COLORS, PATHS } from '@/constants';
import { normalizeHTML, getAddress } from '@/common';
import {
  useWatchlistCard,
  useSearchParamsCompat,
  useImageObserver,
  writeListingNavState,
} from '@/hooks';
import { CopyIcon, GeoIcon, HeartIcon } from '@/components';
import { usePathname } from 'next/navigation';
import isEqual from 'react-fast-compare';
import { GalleryThumbImage } from '@/components/shared/GalleryThumbImage/GalleryThumbImage';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

const QuickRequestModal = dynamic(() =>
  import('@/components/modals/QuickRequestModal').then((m) => ({ default: m.QuickRequestModal })),
);
const ShareModal = dynamic(() =>
  import('@/components/modals/ShareModal').then((m) => ({ default: m.ShareModal })),
);

interface HouseListCardHorizontalProjectProps {
  data: IHousingObjectResponse;
  id?: string;
  offerType?: OfferType;
  fromList: boolean;
  priority?: boolean;
  hideBroker?: boolean;
  isProject?: boolean;
}

// eslint-disable-next-line react/display-name
export const HouseListCardHorizontalProject: FC<HouseListCardHorizontalProjectProps> = memo(
  ({ data, id, offerType, fromList = false, priority: _priority, hideBroker }) => {
    const t = useTranslations();
    const pathname = usePathname();
    const searchParams = useSearchParamsCompat();
    const { inWatchlist, toggleWatch } = useWatchlistCard(data, true);
    const {
      isBroken: brokerImgBroken,
      imgSrc: brokerImgSrc,
      handleError: brokerImgError,
      handleLoad: brokerImgLoad,
    } = useImageObserver(data?.brokerData?.imageFullUrl ?? '');

    const baseSlug = `${data.slug}__${data.id}`;

    const listPageUrl = fromList
      ? (() => {
          const params = new URLSearchParams(searchParams);
          params.delete('fromListUrl');
          params.delete('fromDetailId');
          const search = params.toString();
          return `${pathname}${search ? `?${search}` : ''}`;
        })()
      : '';

    const slugLink = baseSlug;

    const handleCardClick = () => {
      if (fromList && listPageUrl) {
        writeListingNavState(listPageUrl, data.id);
      }
    };

    const projectHref = `/${PATHS.PROJECT}/${slugLink}`;
    const pd = data.projectData;
    const numFound = pd?.immoNumFound;

    const hasObjects = pd?.immoObjects && pd.immoObjects.length > 0;

    const allImages = (data.imageList ?? [])
      .map((img) => img?.imageMidUrl || img?.imageFullUrl || img?.imageTmbUrl || '')
      .filter(Boolean) as string[];
    const mainSrc = allImages[0];
    const thumbSrcs = allImages.slice(1, 7);
    const extraCount = Math.max(0, allImages.length - 7);

    return (
      <Wrapper id={id} $isProject>
        <ImageWrapper>
          <ProjectGalleryGrid>
            <ProjectGalleryMain>
              {mainSrc && (
                <Link
                  href={`/${PATHS.PROJECT}/${slugLink}`}
                  style={{ display: 'block', width: '100%', height: '100%' }}
                  onClick={handleCardClick}
                >
                  <img
                    src={mainSrc}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Link>
              )}
            </ProjectGalleryMain>

            {Array.from({ length: 6 }, (_, i) => {
              const src = thumbSrcs[i];
              return (
                <ProjectGalleryThumb key={i} $index={i} $empty={!src}>
                  {src && (
                    <Link
                      href={`/${PATHS.PROJECT}/${slugLink}`}
                      style={{ display: 'block', width: '100%', height: '100%' }}
                      onClick={handleCardClick}
                    >
                      <img
                        src={src}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {i === thumbSrcs.length - 1 && extraCount > 0 && (
                        <ProjectGalleryMore>+{extraCount}</ProjectGalleryMore>
                      )}
                    </Link>
                  )}
                </ProjectGalleryThumb>
              );
            })}
          </ProjectGalleryGrid>
        </ImageWrapper>

        <ContentWrapper>
          <InfoWrapper>
            {/* Broker office name above title */}
            {!hideBroker && data.brokerData?.officeName && (
              <ProjectOfficeName fontSize={14} fontWeight={600} lineHeight="126%">
                {data.brokerData.officeName}
              </ProjectOfficeName>
            )}

            <CardHead>
              <Link href={projectHref} style={{ width: '100%' }} onClick={handleCardClick}>
                <ProjectCardTitle
                  fontSize={22}
                  fontWeight={700}
                  lineHeight="126%"
                  color={COLORS['primary/600']}
                >
                  {normalizeHTML(data.projectData?.title ?? data.title)}
                </ProjectCardTitle>
              </Link>
              <LocationWrapper>
                <GeoIcon />
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  lineHeight="24px"
                  textTransform="capitalize"
                  noWrap
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}
                >
                  {getAddress({
                    address: data.address,
                    postcode: data.postcode,
                    place: data.place,
                  })}
                </Typography>
              </LocationWrapper>
            </CardHead>

            {/* Description */}
            <ProjectCardDescription fontWeight={400} fontSize={14} lineHeight="145%">
              {normalizeHTML(data?.description)}
            </ProjectCardDescription>

            {/* Project stats (ranges) */}
            <ProjectCardStats data={data} offerType={offerType} inline />

            {/* Project objects list */}
            {hasObjects && (
              <div>
                <ProjectObjectsTitle>
                  <Typography fontSize={13} fontWeight={600} color={COLORS['primary/900']}>
                    {t('projectDetails.objectsTitle', {
                      shown: Math.min(pd!.immoObjects!.length, 3),
                      total: numFound ?? pd!.immoObjects!.length,
                    })}
                  </Typography>
                </ProjectObjectsTitle>
                <ProjectObjectsList>
                  {pd!.immoObjects!.slice(0, 3).map((obj) => {
                    const thumb = obj.imageTmbUrl || obj.imageList?.[0]?.imageTmbUrl;
                    const isSell = obj.sell;
                    const price = isSell
                      ? obj.prices?.sellPrice || null
                      : obj.prices?.monthlyCosts || obj.prices?.grossRent || null;
                    const area = obj.squares?.areaDetailed;
                    const rooms = obj.otherDetails?.roomNumber;
                    const titleLine = [
                      rooms ? `${rooms} ${t('shortRooms')}` : null,
                      area
                        ? area.replace(/ - /g, '\u00A0–\u00A0').replace(/ m²/, '\u00A0m²')
                        : null,
                    ]
                      .filter(Boolean)
                      .join(', ');
                    const subtitle = isSell ? t('sell') : t('rent');
                    const objectHref = `/${PATHS.HOUSING}/${obj.slug}__${obj.id}`;
                    return (
                      <ProjectObjectsRow key={obj.id} href={objectHref} onClick={handleCardClick}>
                        <ProjectObjectThumb>
                          <GalleryThumbImage src={thumb} />
                        </ProjectObjectThumb>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            fontSize={13}
                            fontWeight={600}
                            color={COLORS['primary/600']}
                            noWrap
                          >
                            {titleLine || normalizeHTML(obj.title)}
                          </Typography>
                          <Typography fontSize={12} color={COLORS['neutral/500']} noWrap>
                            {subtitle}
                          </Typography>
                        </div>
                        {price && (
                          <Typography
                            fontSize={13}
                            fontWeight={600}
                            color={COLORS['primary/900']}
                            noWrap
                          >
                            {price}
                          </Typography>
                        )}
                      </ProjectObjectsRow>
                    );
                  })}
                </ProjectObjectsList>
              </div>
            )}

            {/* Bottom: broker + buttons */}
            <ProjectBottomBar>
              {/* Broker info */}
              {!hideBroker && (
                <BrokerInfoContainer>
                  {brokerImgSrc && !brokerImgBroken && (
                    <BrokerImage
                      src={brokerImgSrc}
                      alt={data?.brokerData?.name ?? ''}
                      onError={brokerImgError}
                      onLoad={brokerImgLoad}
                    />
                  )}
                  <BrokerInfoColumn>
                    {data?.brokerData?.name && (
                      <Typography
                        fontSize={12}
                        fontWeight={700}
                        lineHeight="125%"
                        noWrap
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {data.brokerData.name}
                      </Typography>
                    )}
                    {data?.brokerData?.officeName && (
                      <Typography
                        fontSize={12}
                        fontWeight={500}
                        lineHeight="125%"
                        color={COLORS['neutral/600']}
                        noWrap
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {data.brokerData.officeName}
                      </Typography>
                    )}
                  </BrokerInfoColumn>
                </BrokerInfoContainer>
              )}

              {/* Buttons */}
              <ProjectButtonsContainer>
                <ProjectViewButton href={projectHref} onClick={handleCardClick}>
                  {t('searchResult.showAll')}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M3.3335 8H12.6668"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8 3.3335L12.6667 8.00016L8 12.6668"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </ProjectViewButton>
                {!hideBroker &&
                  (data?.brokerData?.contactEmail ||
                    data?.brokerData?.officeName ||
                    data?.brokerData?.name) && (
                    <QuickRequestModal
                      objectId={data.id}
                      whatsappDisabled={data.whatsappDisabled}
                      hasInfoRequest={data.hasInfoRequest}
                      brokerData={{
                        imageFullUrl: data?.brokerData?.imageFullUrl,
                        name: data?.brokerData?.name,
                        officeName: data?.brokerData?.officeName,
                      }}
                      trigger={
                        <ProjectQuickRequestButton type="button">
                          {t('quickRequest')}
                        </ProjectQuickRequestButton>
                      }
                    />
                  )}
              </ProjectButtonsContainer>
            </ProjectBottomBar>
          </InfoWrapper>

          <ButtonsBox>
            <StyledButton
              onClick={toggleWatch}
              aria-label={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
              sx={{ color: `${COLORS['secondary/500']} !important` }}
            >
              <HeartIcon
                isActive={inWatchlist}
                width={19}
                height={19}
                stroke="#EC5B5B"
                aria-hidden="true"
              />
            </StyledButton>
            <ShareModal
              id={data.id}
              title={data.title}
              media={data.imageFullUrl}
              slugLink={slugLink}
              trigger={
                <StyledButton aria-label="Share">
                  <CopyIcon width={19} height={19} aria-hidden="true" />
                </StyledButton>
              }
            />
          </ButtonsBox>
        </ContentWrapper>
      </Wrapper>
    );
  },
  isEqual,
);
